import React, { useEffect, useId, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  CircularProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { formatMoney, getPricing, checkInvoice } from "./subscriptionPricing";
import { safeDate, safeDateTime } from "../../utils";
import { formatTrialDate } from "./subscriptionTrialDates";
import { getAccountPromos } from "../accounts/accountPromoUtils";

import { subscriptionCardSx } from "./subscriptionStyles";
import SubscriptionPlanAction from "./SubscriptionPlanAction";
import SubscriptionDiscountAction from "./SubscriptionDiscountAction";
import SubscriptionOwnershipAction from "./SubscriptionOwnershipAction";
import SubscriptionOfflinePaymentAction from "./SubscriptionOfflinePaymentAction";
import SubscriptionTrialAction from "./SubscriptionTrialAction";
import SubscriptionSubUsersAction from "./SubscriptionSubUsersAction";
import SubscriptionRenewCycleAction from "./SubscriptionRenewCycleAction";
import SubscriptionActionDialog from "./SubscriptionActionDialog";
import SubscriptionBillingDrawer from "./SubscriptionBillingDrawer";
export { subscriptionCardSx } from "./subscriptionStyles";
export { default as DiscountPreview } from "./SubscriptionDiscountPreview";

const PROMO_STATUS_LABELS = {
  ISSUED: "Not yet redeemed",
  REDEEMED: "Redeemed",
  EXPIRED: "Expired",
};

const ACTIONS = [
  {
    key: "plan",
    group: "pricing",
    label: "Change plan",
    // description:
    //   "Choose a new plan for this subscription. Review the change before saving.",
    icon: <SwapHorizIcon />,
    component: SubscriptionPlanAction,
  },
  {
    key: "discount",
    group: "pricing",
    label: "Set discount",
    dialogIcon: <PercentOutlinedIcon />,
    icon: <LocalOfferOutlinedIcon />,
    component: SubscriptionDiscountAction,
  },
  {
    key: "ownership",
    group: "members",
    label: "Change owner",
    // description:
    //   "Transfer this subscription to a verified member of the organization.",
    icon: <PersonOutlineIcon />,
    component: SubscriptionOwnershipAction,
  },
  {
    key: "offline",
    group: "billing",
    label: "Record offline payment",
    // description:
    //   "Record a payment received by bank transfer, cheque, or another method outside HitPay.",
    icon: <PaymentsOutlinedIcon />,
    component: SubscriptionOfflinePaymentAction,
  },
  {
    key: "trial",
    group: "lifecycle",
    label: "Manage trial",
    // description: "Adjust the trial end date or start a new trial period.",
    icon: <EventOutlinedIcon />,
    component: SubscriptionTrialAction,
  },
  {
    key: "subUsers",
    disabled: true, // Re-enable after testing is complete.
    group: "members",
    maxWidth: "lg",
    contentSx: { scrollbarGutter: "stable" },
    label: "Manage sub-users",
    icon: <GroupOutlinedIcon />,
    component: SubscriptionSubUsersAction,
  },
  {
    key: "renewCycle",
    disabled: true, // Re-enable after testing is complete.
    group: "lifecycle",
    label: "Prepare renewal",
    icon: <AutorenewIcon />,
    component: SubscriptionRenewCycleAction,
  },
];

const ACTION_GROUPS = [
  { key: "pricing", label: "Plan & pricing" },
  { key: "lifecycle", label: "Trial & renewal" },
  { key: "members", label: "Members & ownership" },
  { key: "billing", label: "Billing & payments" },
];

export default function SubscriptionPricingPanel({
  subscription,
  user,
  refreshedAt,
  error,
  loading,
  onRefresh,
  actions = {},
  payments = [],
  audit = [],
  completedAction,
}) {
  const [activeAction, setActiveAction] = useState(null);
  const actionsId = useId();
  const [billingOpen, setBillingOpen] = useState(false);
  const selectedAction = ACTIONS.find(
    (action) => action.key === activeAction && !action.disabled
  );
  const ActionForm = selectedAction?.component;
  const actionsDisabled = loading || Boolean(error);
  useEffect(() => {
    if (completedAction)
      setActiveAction((current) =>
        current === completedAction.key ? null : current
      );
  }, [completedAction]);
  const p = getPricing(subscription);
  const owner = user || subscription.owner || {};
  const promoOwner = subscription.owner || owner;
  const ownerPromos = getAccountPromos(promoOwner);
  const money = (value) => formatMoney(value, p.currency);
  const status = subscription.status || "unknown";
  const statusColor =
    {
      active: "success",
      trialing: "info",
      past_due: "warning",
      expired: "error",
    }[status] || "default";
  const cycle =
    { monthly: "month", yearly: "year", one_time: "one-time" }[p.interval] ||
    "cycle";
  const monthlyMrr = p.mrrEligible && p.interval === "monthly";
  const invoiceIssues = p.unpaid.filter(
    (log) => checkInvoice(log).matches === false
  ).length;
  const hasBillingIssue =
    p.reconciled === false || invoiceIssues > 0 || p.missingCharges;

  const { percent, source } = p.discount;
  const discountDescription =
    percent == null
      ? "Discount information is unavailable"
      : source === "No discount"
        ? source
        : `${source}: ${percent}%`;

  const actionButtonSx = {
    textTransform: "none",
    borderRadius: 1.5,
    fontWeight: 600,
    minHeight: 44,
    minWidth: 0,
    px: 1.25,
    py: 1,
    justifyContent: "flex-start",
    textAlign: "left",
    lineHeight: 1.4,
    overflowWrap: "anywhere",
    color: "text.primary",
    borderColor: (theme) => alpha(theme.palette.primary.main, 0.25),
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
    "& .MuiButton-startIcon": {
      color: "primary.main",
      ml: 0,
      mr: 1.25,
      flexShrink: 0,
      "& > *:nth-of-type(1)": { fontSize: 18 },
    },
    "& .MuiButton-endIcon": {
      ml: "auto",
      pl: 1,
      mr: 0,
      color: "text.secondary",
      "& > *:nth-of-type(1)": { fontSize: 16 },
    },
    "&.Mui-disabled": {
      bgcolor: "action.disabledBackground",
      borderColor: "divider",
      "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: "inherit" },
    },
    "&:hover": {
      color: "primary.main",
      borderColor: "primary.main",
      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
      "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: "inherit" },
    },
    "&:active": {
      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
    },
    "&.Mui-focusVisible": {
      outline: "2px solid",
      outlineColor: "primary.main",
      outlineOffset: -2,
    },
  };

  const formatLastUpdated = (dateValue) => {
    if (!dateValue) return "Not updated yet";

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return "Not updated yet";

    const now = new Date();

    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const time = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });

    let dateLabel;

    if (date >= today && date <= now) {
      dateLabel = "Today";
    } else if (date >= yesterday && date < today) {
      dateLabel = "Yesterday";
    } else {
      dateLabel = date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    }

    return `Updated ${dateLabel}, ${time}`;
  };

  return (
    <Paper
      component='section'
      aria-label='Subscription overview'
      elevation={0}
      sx={{
        ...subscriptionCardSx,
        flex: "1 1 0",
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
      }}
    >
      <Box sx={{ height: 3, flexShrink: 0 }}>
        {loading && (
          <LinearProgress
            aria-label='Refreshing subscription'
            sx={{ height: 3 }}
          />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: "1 0 auto",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent='space-between'
          alignItems={{ sm: "center" }}
          gap={2}
          sx={{
            p: { xs: 2, sm: 2.5 },
          }}
        >
          <Stack
            direction='row'
            gap={1.5}
            alignItems='center'
            sx={{ minWidth: 0 }}
          >
            <Avatar
              variant='rounded'
              sx={{
                width: 46,
                height: 46,
                borderRadius: 3,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                color: "primary.main",
              }}
            >
              <BusinessOutlinedIcon />
            </Avatar>

            <Box sx={{ minWidth: 0 }}>
              <Stack direction='row' alignItems='center' spacing={0.5}>
                <Typography
                  component='h2'
                  variant='h6'
                  fontWeight={750}
                  sx={{ minWidth: 0, overflowWrap: "anywhere" }}
                >
                  {owner.organization_name ||
                    subscription.organization?.name ||
                    owner.name ||
                    "Subscription"}
                </Typography>
                {ownerPromos.length > 0 && (
                  <Tooltip
                    arrow
                    describeChild
                    placement='top'
                    title={
                      <Stack spacing={1} sx={{ p: 0.5, maxWidth: 280 }}>
                        <Typography variant='caption' fontWeight={700}>
                          Promo code details
                        </Typography>
                        {ownerPromos.map((promo) => (
                          <Stack
                            key={promo.id || promo.promo_code}
                            spacing={0.25}
                          >
                            <Typography
                              variant='caption'
                              fontWeight={600}
                              sx={{ overflowWrap: "anywhere" }}
                            >
                              Code: {promo.promo_code}
                            </Typography>
                            {promo.partner_name && (
                              <span>Partner: {promo.partner_name}</span>
                            )}
                            {promo.discount_type === "PERCENTAGE" &&
                              promo.discount_value != null && (
                                <span>
                                  Promo offer: {promo.discount_value}% off
                                </span>
                              )}
                            {promo.status && (
                              <span>
                                Status:{" "}
                                {PROMO_STATUS_LABELS[promo.status] ||
                                  promo.status}
                              </span>
                            )}
                            {promo.redeemed_at && (
                              <span>
                                Redeemed on: {safeDateTime(promo.redeemed_at)}
                              </span>
                            )}
                            {promoOwner.pending_promo_code ===
                              promo.promo_code && (
                              <span>Awaiting signup completion</span>
                            )}
                          </Stack>
                        ))}
                        {subscription.custom_discount_percent != null && (
                          <span>
                            Current discount:{" "}
                            {subscription.custom_discount_percent}%
                          </span>
                        )}
                      </Stack>
                    }
                  >
                    <Chip
                      size='small'
                      label='Code Applied'
                      icon={<LocalOfferOutlinedIcon />}
                      tabIndex={0}
                      aria-label='Code Applied: owner promo code details'
                      sx={{
                        height: 24,
                        borderRadius: 1,
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: "primary.main",
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                        flexShrink: 0,
                        cursor: "help",
                        "& .MuiChip-icon": { color: "inherit", fontSize: 14 },
                        "&:hover, &:focus-visible": {
                          bgcolor: (t) => alpha(t.palette.primary.main, 0.14),
                        },
                      }}
                    />
                  </Tooltip>
                )}
              </Stack>

              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 0.25 }}
              >
                Subscription owner: {owner.name || "Not assigned"}
              </Typography>

              {owner.email && (
                <Stack
                  direction='row'
                  alignItems='center'
                  spacing={0.75}
                  sx={{ mt: 0.25, minWidth: 0 }}
                >
                  <MailOutlineIcon
                    sx={{ fontSize: 15, color: "text.disabled", flexShrink: 0 }}
                  />
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ overflowWrap: "anywhere" }}
                  >
                    {owner.email}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>
          <Stack
            direction='column'
            spacing={0.5}
            alignItems='flex-end'
            sx={{
              alignSelf: { xs: "flex-end", sm: "auto" },
              flexShrink: 0,
            }}
          >
            <Button
              variant='outlined'
              size='small'
              startIcon={
                loading ? (
                  <CircularProgress size={14} color='inherit' />
                ) : (
                  <RefreshIcon />
                )
              }
              disabled={loading}
              onClick={onRefresh}
              sx={{
                textTransform: "none",
                minWidth: 100,
              }}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>

            <Typography
              variant='caption'
              color='text.secondary'
              aria-live='polite'
              sx={{
                fontSize: "0.7rem",
                whiteSpace: "nowrap",
              }}
            >
              {formatLastUpdated(refreshedAt)}
            </Typography>
          </Stack>
        </Stack>
        <Box
          component='section'
          aria-labelledby='subscription-plan-heading'
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: "1 0 auto",
            border: 1,
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: { xs: 2, sm: 2.5 },
              bgcolor: (t) => alpha(t.palette.primary.main, 0.025),
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent='space-between'
              gap={2.5}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  id='subscription-plan-heading'
                  component='h3'
                  variant='overline'
                  color='text.secondary'
                >
                  Plan & pricing
                </Typography>
                <Stack
                  direction='row'
                  alignItems='center'
                  gap={1}
                  flexWrap='wrap'
                  sx={{ mt: 0.5 }}
                >
                  <Typography variant='h6' fontWeight={700}>
                    {subscription.planName ||
                      subscription.plan?.name ||
                      "Plan unavailable"}
                  </Typography>
                  <Chip
                    size='small'
                    color={statusColor}
                    label={
                      {
                        active: "Active",
                        trialing: "Free trial",
                        past_due: "Payment overdue",
                        expired: "Expired",
                        cancelled: "Cancelled",
                        pending: "Pending",
                      }[status] || status.replace(/_/g, " ")
                    }
                    sx={{ textTransform: "capitalize", fontWeight: 600 }}
                  />
                </Stack>

                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mt: 1 }}
                >
                  {status === "trialing" ? (
                    <>
                      Trial ends:{" "}
                      <Box
                        component='span'
                        sx={{ fontWeight: 600, color: "text.primary" }}
                      >
                        {formatTrialDate(
                          subscription.trialEnd || subscription.trial_end
                        )}
                      </Box>
                    </>
                  ) : subscription.renewal === false ? (
                    <>
                      Automatic renewal:{" "}
                      <Box
                        component='span'
                        sx={{ fontWeight: 600, color: "error.main" }}
                      >
                        Disabled
                      </Box>
                    </>
                  ) : (
                    <>
                      Next billing:{" "}
                      <Box
                        component='span'
                        sx={{ fontWeight: 600, color: "text.primary" }}
                      >
                        {safeDate(
                          subscription.nextBilling ||
                            subscription.next_billing_date
                        )}
                      </Box>
                    </>
                  )}
                </Typography>

                {subscription.renewal !== false && (
                  <Stack
                    direction='row'
                    spacing={1}
                    alignItems='center'
                    sx={{ mt: 0.5 }}
                  >
                    <Typography variant='body2' color='text.secondary'>
                      Automatic Renewal:
                    </Typography>

                    <Chip
                      label={
                        subscription.renewal === true
                          ? "Enabled"
                          : "Not confirmed"
                      }
                      color={
                        subscription.renewal === true ? "success" : "default"
                      }
                      size='small'
                      variant='outlined'
                      sx={{
                        height: 22,
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        borderRadius: 1,
                      }}
                    />
                  </Stack>
                )}
              </Box>
              <Box sx={{ minWidth: 0, textAlign: { xs: "left", md: "right" } }}>
                <Typography variant='body2' color='text.secondary'>
                  {p.interval === "one_time"
                    ? "One-time plan price"
                    : "Plan price per " + cycle}
                  {p.derivedRecurring == null ? " (estimated)" : ""}
                </Typography>
                <Typography
                  variant='h4'
                  fontWeight={750}
                  sx={{
                    mt: 0.25,
                    fontVariantNumeric: "tabular-nums",
                    overflowWrap: "anywhere",
                  }}
                >
                  {p.recurring == null
                    ? "Price unavailable"
                    : money(p.recurring)}
                </Typography>
                <Typography variant='body2' sx={{ mt: 0.75, fontWeight: 600 }}>
                  {discountDescription}
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  display='block'
                  sx={{ mt: 0.5 }}
                >
                  Excludes previous unpaid charges. See Billing details.
                </Typography>
                {!monthlyMrr && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    display='block'
                    sx={{ mt: 0.5 }}
                  >
                    Monthly recurring revenue (MRR):{" "}
                    {p.mrr == null ? "Unavailable" : money(p.mrr)}
                    {p.mrrEligible && p.derivedRecurring == null
                      ? " (estimated)"
                      : ""}
                    {p.mrr === 0 && !p.mrrEligible
                      ? " (only active plans with automatic renewal count)"
                      : ""}
                  </Typography>
                )}
              </Box>
            </Stack>
            {error ? (
              <Alert severity='warning' sx={{ mt: 2 }}>
                The latest subscription details could not be confirmed. These
                are the last saved details. Refresh before making changes.
              </Alert>
            ) : hasBillingIssue ? (
              <Alert severity='warning' sx={{ mt: 2 }}>
                {p.reconciled === false
                  ? "The billing amount does not match the plan price and saved discount. Check Billing details before relying on this price."
                  : "Some previous charges need review. Open Billing details to check their amounts and discounts."}
              </Alert>
            ) : p.reconciled === true ? (
              <></>
            ) : (
              <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
                There is not enough information to confirm this price. Open
                Billing details to see what is missing.
              </Typography>
            )}
          </Box>
          <Box
            component='section'
            aria-label='Subscription management'
            sx={{
              p: { xs: 2, sm: 2.5 },
              borderTop: 1,
              borderColor: "divider",
              containerType: "inline-size",
              containerName: "subscription-actions",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr)",
                rowGap: 2,
                "& > section + section": {
                  pt: 2,
                  borderTop: 1,
                  borderColor: "divider",
                },
                "@container subscription-actions (min-width: 520px)": {
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  rowGap: 2.5,
                  "& > section + section": { pt: 0, borderTop: 0 },
                  "& > section:nth-of-type(odd)": { pr: 2.5 },
                  "& > section:nth-of-type(even)": {
                    pl: 2.5,
                    borderLeft: 1,
                    borderColor: "divider",
                  },
                },
                "@container subscription-actions (min-width: 1040px)": {
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  "& > section:nth-of-type(n)": { px: 2.5 },
                  "& > section + section": {
                    borderLeft: 1,
                    borderColor: "divider",
                  },
                  "& > section:first-of-type": { pl: 0 },
                  "& > section:last-of-type": { pr: 0 },
                },
              }}
            >
              {ACTION_GROUPS.map((group) => (
                <Box
                  key={group.key}
                  component='section'
                  aria-labelledby={`${actionsId}-${group.key}`}
                  sx={{ minWidth: 0 }}
                >
                  <Typography
                    id={`${actionsId}-${group.key}`}
                    component='h4'
                    variant='overline'
                    fontWeight={600}
                    color='text.secondary'
                    sx={{
                      px: 1,
                      mb: 0.75,
                      lineHeight: 1.75,
                      letterSpacing: "0.06em",
                    }}
                  >
                    {group.label}
                  </Typography>
                  <Stack spacing={1}>
                    {ACTIONS.filter((action) => action.group === group.key).map(
                      (action) => (
                        <Button
                          key={action.key}
                          fullWidth
                          size='small'
                          variant='outlined'
                          startIcon={action.icon}
                          disabled={
                            action.disabled || actionsDisabled || !actions[action.key]
                          }
                          onClick={() => setActiveAction(action.key)}
                          sx={actionButtonSx}
                        >
                          {action.label}
                        </Button>
                      )
                    )}
                    {group.key === "billing" && (
                      <Button
                        fullWidth
                        size='small'
                        variant='outlined'
                        startIcon={<ReceiptLongOutlinedIcon />}
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => setBillingOpen(true)}
                        sx={actionButtonSx}
                      >
                        Billing details
                      </Button>
                    )}
                  </Stack>
                </Box>
              ))}
            </Box>
          </Box>
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              mt: "auto",
              borderTop: 1,
              flexShrink: 0,
              borderColor: "divider",
              "&:before": { display: "none" },
              bgcolor: (t) => alpha(t.palette.primary.main, 0.015),
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ px: { xs: 2, sm: 2.5 } }}
            >
              <Typography variant='body2' fontWeight={600}>
                How this price is calculated
              </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ px: { xs: 2, sm: 2.5 }, pt: 0 }}>
              <Typography variant='body2' color='text.secondary'>
                This is an estimated subscription cost, not a confirmed charge.
                The saved subscription discount, including promo discounts,
                takes priority over the Beta program discount, even at 0%. These
                discounts do not stack.
              </Typography>

              {p.catalogMismatch && (
                <Alert severity='warning' sx={{ mt: 2 }}>
                  The listed plan price differs from the price currently used
                  for billing. The estimate below uses the current billing
                  price.
                </Alert>
              )}

              <Typography
                variant='subtitle2'
                fontWeight={700}
                sx={{ mt: 3, mb: 1 }}
              >
                Estimated Subscription Cost
              </Typography>

              <TableContainer>
                <Table
                  size='small'
                  aria-label='Subscription price breakdown'
                  sx={{
                    "& td:last-child": {
                      whiteSpace: "nowrap",
                      fontVariantNumeric: "tabular-nums",
                    },
                  }}
                >
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        Users included in billing
                        <Typography
                          variant='caption'
                          display='block'
                          color='text.secondary'
                        >
                          {p.seats ?? "Unknown"} × {money(p.seatRate)}
                          {" per user"}
                        </Typography>
                      </TableCell>
                      <TableCell align='right'>{money(p.seatBase)}</TableCell>
                    </TableRow>

                    {subscription.plan?.name ===
                      "In-house Tax Practitioner" && (
                      <TableRow>
                        <TableCell>
                          Additional active companies
                          <Typography
                            variant='caption'
                            display='block'
                            color='text.secondary'
                          >
                            5 companies included per user.
                            <br />
                            {p.extraCompanies ?? "Unknown"} × {money(300)}
                            {" per additional company"}
                          </Typography>
                        </TableCell>
                        <TableCell align='right'>
                          {money(p.companyBase)}
                        </TableCell>
                      </TableRow>
                    )}

                    <TableRow>
                      <TableCell>Subtotal before discount</TableCell>
                      <TableCell align='right'>{money(p.base)}</TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell>
                        Discount (
                        {p.discount.percent == null
                          ? "Unknown"
                          : `${p.discount.percent}%`}
                        )
                      </TableCell>
                      <TableCell align='right'>
                        {p.base != null && p.expectedRecurring != null
                          ? `− ${money(p.base - p.expectedRecurring)}`
                          : "—"}
                      </TableCell>
                    </TableRow>

                    <TableRow
                      sx={{
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
                      }}
                    >
                      <TableCell sx={{ fontWeight: 700 }}>
                        Estimated total per billing cycle
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{
                          fontWeight: 700,
                          color: "primary.main",
                        }}
                      >
                        {money(p.expectedRecurring)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 2.5 }} />

              <Typography variant='subtitle2' fontWeight={700} sx={{ mb: 1.5 }}>
                Subscription Information
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                }}
              >
                {[
                  {
                    label: "Monthly recurring revenue (MRR)",
                    value:
                      (p.mrr == null ? "Unavailable" : money(p.mrr)) +
                      (p.mrrEligible && p.derivedRecurring == null
                        ? " (estimated)"
                        : ""),
                  },
                  {
                    label: "Subscription ID",
                    value: subscription.id || subscription.subId || "Unknown",
                  },
                  {
                    label: "Owner ID",
                    value: owner.u_id || subscription.owner_id || "Unknown",
                  },
                  {
                    label: "Beta program",
                    value:
                      (subscription.isOpenBeta ?? subscription.is_open_beta) ==
                      null
                        ? "Unknown"
                        : (subscription.isOpenBeta ?? subscription.is_open_beta)
                          ? "Enrolled"
                          : "Not enrolled",
                  },
                  {
                    label: "Payment setup",
                    value: subscription.verified ? "Verified" : "Not verified",
                  },
                ].map((item) => (
                  <Box key={item.label} sx={{ minWidth: 0 }}>
                    <Typography variant='caption' color='text.secondary'>
                      {item.label}
                    </Typography>
                    <Typography
                      variant='body2'
                      fontWeight={500}
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Typography
                variant='caption'
                color='text.disabled'
                sx={{ display: "block", mt: 2.5 }}
              >
                {formatLastUpdated(refreshedAt)}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
      <SubscriptionActionDialog
        title={selectedAction?.label || "Subscription action"}
        maxWidth={selectedAction?.maxWidth}
        contentSx={selectedAction?.contentSx}
        description={selectedAction?.description}
        icon={selectedAction?.dialogIcon || selectedAction?.icon}
        subscription={subscription}
        open={Boolean(selectedAction)}
        onClose={() => setActiveAction(null)}
        loading={loading}
        error={error}
      >
        {ActionForm && <ActionForm {...actions[activeAction]} />}
      </SubscriptionActionDialog>
      <SubscriptionBillingDrawer
        open={billingOpen}
        onClose={() => setBillingOpen(false)}
        subscription={subscription}
        payments={payments}
        audit={audit}
        error={error}
      />
    </Paper>
  );
}
